export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'AUTH_ERROR'
  | 'PERMISSION_ERROR'
  | 'COMPLIANCE_ERROR'
  | 'ENROLLMENT_ERROR'
  | 'PAYMENT_ERROR'
  | 'QUIZ_ERROR'
  | 'PROGRESS_ERROR'
  | 'LESSON_ERROR'
  | 'MODULE_ERROR'
  | 'COURSE_ERROR'
  | 'PVQ_ERROR'
  | 'SCHEDULING_ERROR'
  | 'SECURITY_ERROR'
  | 'USER_ERROR'
  | 'SERVICE_UNAVAILABLE'
  | 'UNKNOWN_ERROR'
  | 'FIREBASE_ERROR'
  | string;

export class ApiError extends Error {
  code: ApiErrorCode;
  originalError: unknown | null;
  timestamp: string;

  constructor(code: ApiErrorCode, message: string, originalError: unknown = null) {
    super(message);
    this.code = code;
    this.message = message;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
    // Maintain prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }

  toJSON() {
    return {
      error: {
        code: this.code,
        message: this.message,
        timestamp: this.timestamp
      }
    };
  }
}

export class ValidationError extends ApiError {
  fields: string[];

  constructor(message: string, fields: string[] = []) {
    super('VALIDATION_ERROR', message);
    this.fields = fields;
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class NotFoundError extends ApiError {
  resource: string | unknown;
  id: string | unknown;

  constructor(resource: string | unknown, id?: string | unknown) {
    super('NOT_FOUND', `${resource} with id ${id} not found`);
    this.resource = resource;
    this.id = id;
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

export class AuthError extends ApiError {
  constructor(message: string = 'Authentication required') {
    super('AUTH_ERROR', message);
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

export class PermissionError extends ApiError {
  constructor(message: string = 'Permission denied') {
    super('PERMISSION_ERROR', message);
    Object.setPrototypeOf(this, PermissionError.prototype);
  }
}

export class ComplianceError extends ApiError {
  details: Record<string, unknown>;

  constructor(message: string, details: Record<string, unknown> = {}) {
    super('COMPLIANCE_ERROR', message);
    this.details = details;
    Object.setPrototypeOf(this, ComplianceError.prototype);
  }
}

export class EnrollmentError extends ApiError {
  enrollment: unknown | null;

  constructor(message: string, enrollment: unknown = null) {
    super('ENROLLMENT_ERROR', message);
    this.enrollment = enrollment;
    Object.setPrototypeOf(this, EnrollmentError.prototype);
  }
}

export class PaymentError extends ApiError {
  paymentId: string | null;

  constructor(message: string, paymentId: string | null = null) {
    super('PAYMENT_ERROR', message);
    this.paymentId = paymentId;
    Object.setPrototypeOf(this, PaymentError.prototype);
  }
}

export class QuizError extends ApiError {
  quizId: string | null;

  constructor(message: string, quizId: string | null = null) {
    super('QUIZ_ERROR', message);
    this.quizId = quizId;
    Object.setPrototypeOf(this, QuizError.prototype);
  }
}

export class ProgressError extends ApiError {
  courseId: string | null;

  constructor(message: string, courseId: string | null = null) {
    super('PROGRESS_ERROR', message);
    this.courseId = courseId;
    Object.setPrototypeOf(this, ProgressError.prototype);
  }
}

export class LessonError extends ApiError {
  lessonId: string | null;

  constructor(message: string, lessonId: string | null = null) {
    super('LESSON_ERROR', message);
    this.lessonId = lessonId;
    Object.setPrototypeOf(this, LessonError.prototype);
  }
}

export class ModuleError extends ApiError {
  moduleId: string | null;

  constructor(message: string, moduleId: string | null = null) {
    super('MODULE_ERROR', message);
    this.moduleId = moduleId;
    Object.setPrototypeOf(this, ModuleError.prototype);
  }
}

export class CourseError extends ApiError {
  courseId: string | null;

  constructor(message: string, courseId: string | null = null) {
    super('COURSE_ERROR', message);
    this.courseId = courseId;
    Object.setPrototypeOf(this, CourseError.prototype);
  }
}

export class PVQError extends ApiError {
  questionId: string | null;

  constructor(message: string, questionId: string | null = null) {
    super('PVQ_ERROR', message);
    this.questionId = questionId;
    Object.setPrototypeOf(this, PVQError.prototype);
  }
}

export class SchedulingError extends ApiError {
  slotId: string | null;

  constructor(message: string, slotId: string | null = null) {
    super('SCHEDULING_ERROR', message);
    this.slotId = slotId;
    Object.setPrototypeOf(this, SchedulingError.prototype);
  }
}

export class SecurityError extends ApiError {
  userId: string | null;

  constructor(message: string, userId: string | null = null) {
    super('SECURITY_ERROR', message);
    this.userId = userId;
    Object.setPrototypeOf(this, SecurityError.prototype);
  }
}

export class UserError extends ApiError {
  userId: string | null;

  constructor(message: string, userId: string | null = null) {
    super('USER_ERROR', message);
    this.userId = userId;
    Object.setPrototypeOf(this, UserError.prototype);
  }
}

interface FirebaseError {
  code: string;
  message: string;
}

export const mapFirebaseError = (error: FirebaseError | any): ApiError => {
  const errorMap: Record<string, ApiError> = {
    'auth/user-not-found': new AuthError('User not found'),
    'auth/wrong-password': new AuthError('Invalid credentials'),
    'auth/email-already-in-use': new AuthError('Email already registered'),
    'auth/weak-password': new ValidationError('Password must be at least 6 characters'),
    'permission-denied': new PermissionError('Access denied by Firestore rules'),
    'not-found': new NotFoundError('Document'),
    'unavailable': new ApiError('SERVICE_UNAVAILABLE', 'Service temporarily unavailable')
  };

  const code = (error as FirebaseError).code;
  const mappedError = code ? errorMap[code] : undefined;

  if (mappedError) return mappedError;

  return new ApiError(
    'UNKNOWN_ERROR',
    error.message || 'An unexpected error occurred',
    error
  );
};
